import type { Object3D } from 'three';
import { Group, PointLight } from 'three';
import { EMIT, LIGHTS } from '../render/palette';
import {
  bollard,
  cafeTable,
  directoryPylon,
  downlight,
  escalator,
  exitSign,
  facadeSign,
  fluorescentPanel,
  forecourtMarkings,
  lampPost,
  lightWash,
  litterBin,
  mannequin,
  palletStack,
  pendantLamp,
  planter,
  railing,
  securityCamera,
  sodiumLamp,
  stockCage,
} from './props';
import {
  chairStack,
  chuteConsole,
  controlDesk,
  deskLamp,
  dockBumper,
  dockPlatform,
  ductRun,
  dumpster,
  electricalPanel,
  entranceDoors,
  handTruck,
  keyCabinet,
  lockerBank,
  menuBoard,
  monitorWall,
  mopBucket,
  pinBoard,
  pneumaticChute,
  posterCase,
  radioCharger,
  rollerDoor,
  serverRack,
  stairFlight,
  toolBench,
  vendingMachine,
} from './fixtures';
import {
  aerialMast,
  bayLines,
  chainLinkFence,
  condenserUnit,
  distantBlock,
  getawayVan,
  kerbBlock,
  lotGate,
  parkedCar,
  pylonSign,
  roofFlood,
  satelliteDish,
  stairPenthouse,
  treeLine,
  trolleyBay,
} from './exterior';
import {
  drainedFountain,
  giantBear,
  kiddieRide,
  maintenanceHoarding,
  queueMaze,
  softPlay,
  strayBalloon,
} from './liminal';
import { BACK_DOOR_H, BACK_DOOR_W, hasBackDoor, unitCentres, withDoor } from './plan';
import type {
  MallPlan,
  PatrolRoute,
  PropDef,
  StairDef,
  UnitDef,
  WallDef,
  ZoneDef,
} from './plan';

/**
 * MERIDIAN CROSSING — the fixed map.
 *
 * One building, hand-authored, never generated. Everything below is in metres
 * and the whole thing is legible as a floorplan:
 *
 *                       z = -68  ┌──────── FOOD COURT ────────┐
 *                       z = -44  ├──────┬──────────────┬──────┤
 *                                │ svc  │              │ svc  │
 *   CONTROL ROOM ────── x=-24.5 ─┤ west │   ATRIUM     │ east ├─ x=+24.5 ── LOADING DOCK
 *                                │      │  (2 levels)  │      │
 *                       z = +24  └──────┴───── ▽ ──────┴──────┘
 *                                          SOUTH PLAZA (extraction)
 *
 * The three bands each side are: shops at |x| 13–21, service corridor at
 * |x| 21–24.5, and the atrium between |x| < 13. Level 1 sits at y = 5.2 with the
 * middle strip |x| < 7 left open, so from the upper gallery you look down on
 * everyone below — and they look up at you.
 *
 * Why this shape: a thief's route has to be a loop, not a corridor. Shops open
 * onto the atrium (bright, watched, fast) but every shop also backs onto the
 * service corridor (dark, unwatched, slow, and where the drop-off chutes are).
 * Every trip out with an armful of loot is a choice between those two.
 */

const ATRIUM = 13; // shopfront line
const BACK = 21; // back of the shop band / front of the service corridor
const SHELL = 24.5; // outer wall
const Z_S = 24; // south wall
const Z_N = -44; // where the mall proper ends and the food court begins
const Z_FC = -68; // back of the food court
const L1 = 5.2; // upper level
const H0 = 4.2; // shop height, level 0
const H1 = 4.0; // shop height, level 1
const CANOPY = 12; // atrium glazing
const SVC_CEIL = 2.9; // service corridor headroom — deliberately oppressive

const DEPTH = BACK - ATRIUM; // 8m shop depth

// Control room. Small on purpose: the guard should feel bunkered in, not housed
// in an office. Every metre of empty floor here is a metre of nothing to look at.
const CTRL_X = -32.5;
const CTRL_Z0 = -7;
const CTRL_Z1 = 1;

// ---------------------------------------------------------------------------
// Zones
// ---------------------------------------------------------------------------

const zones: ZoneDef[] = [
  // The atrium and its glass roof.
  { id: 'atrium', x0: -ATRIUM, x1: ATRIUM, z0: Z_N, z1: Z_S, y: 0, floor: 'tile', fog: 'atrium' },
  {
    id: 'canopy',
    x0: -ATRIUM,
    x1: ATRIUM,
    z0: Z_N,
    z1: Z_S,
    y: CANOPY,
    floor: 'none',
    fog: 'atrium',
    ceilingY: CANOPY,
    ceiling: 'glazed',
  },

  // Shop bands, level 0. No ceiling here — each unit builds its own.
  { id: 'shops-w-l0', x0: -BACK, x1: -ATRIUM, z0: Z_N, z1: Z_S, y: 0, floor: 'tileDark', fog: 'corridor' },
  { id: 'shops-e-l0', x0: ATRIUM, x1: BACK, z0: Z_N, z1: Z_S, y: 0, floor: 'tileDark', fog: 'corridor' },

  // Level 1: shop band plus gallery, as one slab each side. The gap between them
  // (|x| < 7) is the void over the atrium.
  { id: 'level1-w', x0: -BACK, x1: -7, z0: Z_N, z1: Z_S, y: L1, floor: 'tile', fog: 'atrium' },
  { id: 'level1-e', x0: 7, x1: BACK, z0: Z_N, z1: Z_S, y: L1, floor: 'tile', fog: 'atrium' },
  // The bridge across the void, where the escalators land.
  { id: 'bridge', x0: -7, x1: 7, z0: -3, z1: 5, y: L1, floor: 'tile', fog: 'atrium' },

  // Service corridors, both levels, running the full length.
  {
    id: 'svc-w-l0', x0: -SHELL, x1: -BACK, z0: Z_FC, z1: Z_S, y: 0,
    floor: 'concrete', fog: 'service', ceilingY: SVC_CEIL, ceiling: 'grid',
  },
  {
    id: 'svc-e-l0', x0: BACK, x1: SHELL, z0: Z_FC, z1: Z_S, y: 0,
    floor: 'concrete', fog: 'service', ceilingY: SVC_CEIL, ceiling: 'grid',
  },
  {
    id: 'svc-w-l1', x0: -SHELL, x1: -BACK, z0: Z_N, z1: Z_S, y: L1,
    floor: 'concrete', fog: 'service', ceilingY: L1 + SVC_CEIL, ceiling: 'grid',
  },
  {
    id: 'svc-e-l1', x0: BACK, x1: SHELL, z0: Z_N, z1: Z_S, y: L1,
    floor: 'concrete', fog: 'service', ceilingY: L1 + SVC_CEIL, ceiling: 'grid',
  },

  // Food court: double height, skylit, chequered floor (reference panel 8).
  {
    id: 'foodcourt', x0: -BACK, x1: BACK, z0: Z_FC, z1: Z_N, y: 0,
    floor: 'checker', fog: 'corridor', ceilingY: 10.5, ceiling: 'glazed', tileSize: 1.1,
  },
  // A balcony finger sticking out over it from level 1.
  { id: 'fc-balcony', x0: -BACK, x1: BACK, z0: Z_N - 5, z1: Z_N, y: L1, floor: 'tile', fog: 'atrium' },

  // Back of house.
  {
    id: 'control', x0: CTRL_X, x1: -SHELL, z0: CTRL_Z0, z1: CTRL_Z1, y: 0,
    floor: 'concrete', fog: 'service', ceilingY: 3.0, ceiling: 'slab',
  },
  {
    id: 'dock', x0: SHELL, x1: 39, z0: -40, z1: -22, y: 0,
    floor: 'concrete', fog: 'parking', ceilingY: 6.0, ceiling: 'slab',
  },

  // Outside. The only way out of the round.
  { id: 'plaza', x0: -17, x1: 17, z0: Z_S, z1: 39, y: 0, floor: 'asphalt', fog: 'exterior' },
  // Entrance canopy only, not a lid over the whole forecourt. It has to sit high
  // in frame on the approach so the eye goes through it to the doors — cover the
  // camera position and the first thing the player ever sees is a ceiling.
  {
    id: 'plaza-canopy', x0: -12, x1: 12, z0: Z_S, z1: 30, y: 5.6,
    floor: 'none', fog: 'exterior', ceilingY: 5.6, ceiling: 'glazed',
  },

  /**
   * THE ROOF.
   *
   * The building had none. Every shell wall stopped at twelve metres and the
   * space above the shop bands was open sky, so from any raised camera the mall
   * was a box you could see the inside of — and more to the point, the roofline
   * against the moon was a cut edge rather than a building. Nothing says
   * unfinished faster.
   *
   * Built as four `slab` ceilings rather than one, because the atrium canopy and
   * the food court glazing have to stay open as light wells — they are where all
   * the moonlight in the building comes from, and lidding them would put the
   * interior into total darkness and invalidate the entire bake. So: a band down
   * each side over the shops, a band down each side over the service corridors,
   * and daylight straight down the middle.
   *
   * At CANOPY exactly, level with the top of the shell, so the parapet reads as
   * the wall continuing past the deck the way a real one does.
   */
  { id: 'roof-w', x0: -SHELL, x1: -ATRIUM, z0: Z_N, z1: Z_S, y: CANOPY, floor: 'none', fog: 'exterior', ceilingY: CANOPY, ceiling: 'slab' },
  { id: 'roof-e', x0: ATRIUM, x1: SHELL, z0: Z_N, z1: Z_S, y: CANOPY, floor: 'none', fog: 'exterior', ceilingY: CANOPY, ceiling: 'slab' },
  { id: 'roof-w-svc', x0: -SHELL, x1: -BACK, z0: Z_FC, z1: Z_N, y: CANOPY, floor: 'none', fog: 'exterior', ceilingY: CANOPY, ceiling: 'slab' },
  { id: 'roof-e-svc', x0: BACK, x1: SHELL, z0: Z_FC, z1: Z_N, y: CANOPY, floor: 'none', fog: 'exterior', ceilingY: CANOPY, ceiling: 'slab' },
  // Over the dock yard, lower, so the roofline steps down at the back corner
  // instead of running one unbroken twelve-metre line for ninety metres.
  { id: 'roof-dock', x0: SHELL, x1: 39, z0: -40, z1: -22, y: 6.0, floor: 'none', fog: 'exterior', ceilingY: 6.0, ceiling: 'slab' },

  /**
   * THE CAR PARK.
   *
   * The other half of the same problem: the world ended at z = 39 on a straight
   * line with no wall, no floor collider and no backdrop, so a player who walked
   * south past the lamp posts stepped off the edge into a void. That is not a
   * missing feature, it is the thing that makes a game read as a demo.
   *
   * Sized to keep the navgrid honest. The grid is a 0.3 m lattice over the union
   * of every zone rectangle and it already spans x −40..39 — so the lot is held
   * to x ±34, inside the dock yard's existing reach, and only grows the world in
   * z. The two side strips fill the dead corners beside the forecourt walls so
   * the apron is one continuous surface rather than a T with hard shoulders.
   */
  { id: 'lot', x0: -34, x1: 34, z0: 39, z1: 64, y: 0, floor: 'asphalt', fog: 'exterior' },
  { id: 'lot-w', x0: -34, x1: -17, z0: Z_S, z1: 39, y: 0, floor: 'asphalt', fog: 'exterior' },
  { id: 'lot-e', x0: 17, x1: 34, z0: Z_S, z1: 39, y: 0, floor: 'asphalt', fog: 'exterior' },
];

// ---------------------------------------------------------------------------
// Walls
// ---------------------------------------------------------------------------

const TALL = CANOPY;

// ---------------------------------------------------------------------------
// Shop units
// ---------------------------------------------------------------------------
// Declared before the walls, not next to the runs that consume them, because the
// service corridor's doors are derived from these unit centres. The two have to
// agree or the back-of-house route silently does not exist.

const vacant = (width: number, label: string, lit = false): UnitDef => ({
  store: null,
  label,
  width,
  lit,
});

/** Pier between adjacent shop units, everywhere. */
const UNIT_GAP = 0.6;
/** Where the first unit of each band starts, measured along +z. */
const L0_FROM = -42;
const L1_FROM = -40;

const westL0: UnitDef[] = [
  vacant(9, 'UNIT AVAILABLE'),
  { store: 'electronics', width: 11 },
  { store: 'jewellery', width: 10 },
  vacant(8, 'SPACE FOR LEASE'),
  { store: 'pharmacy', width: 10 },
  vacant(9, 'COMING SOON'),
];

const eastL0: UnitDef[] = [
  { store: 'supermarket', width: 13 },
  vacant(8, 'FORMER TENANT'),
  { store: 'clothing', width: 10 },
  { store: 'department', width: 12 },
  vacant(8, 'UNIT AVAILABLE'),
  { store: 'skate', width: 9 },
];

const westL1: UnitDef[] = [
  vacant(10, 'RETAIL OPPORTUNITY'),
  { store: 'medieval', width: 12 },
  vacant(10, 'THANK YOU FOR VISITING', true),
  { store: 'guns', width: 12 },
  vacant(11, 'UNIT AVAILABLE'),
];

const eastL1: UnitDef[] = [
  vacant(11, 'UNIT AVAILABLE'),
  vacant(12, 'LEVEL TWO IS OPEN', true),
  vacant(10, 'UNIT AVAILABLE'),
  vacant(12, 'PLEASE ENJOY YOUR VISIT'),
  vacant(10, 'UNIT AVAILABLE'),
];

/** Centres of the units that get a service door, for one band of one wall. */
const doorsFor = (from: number, units: UnitDef[]) =>
  unitCentres(from, units, UNIT_GAP).filter((_, i) => hasBackDoor(units[i]));

const walls: WallDef[] = [
  // --- Outer shell -------------------------------------------------------
  ...withDoor({ axis: 'x', at: Z_S, from: -SHELL, to: SHELL, height: TALL }, 0, 9, 4.6),
  { axis: 'x', at: Z_FC, from: -SHELL, to: SHELL, height: TALL },
  // West shell, interrupted where the control room attaches.
  { axis: 'z', at: -SHELL, from: CTRL_Z1, to: Z_S, height: TALL },
  { axis: 'z', at: -SHELL, from: Z_FC, to: CTRL_Z0, height: TALL },
  ...withDoor({ axis: 'z', at: -SHELL, from: CTRL_Z0, to: CTRL_Z1, height: TALL }, -3, 1.2, 2.2),
  // East shell, interrupted where the loading dock attaches.
  { axis: 'z', at: SHELL, from: -22, to: Z_S, height: TALL },
  { axis: 'z', at: SHELL, from: Z_FC, to: -40, height: TALL },
  ...withDoor({ axis: 'z', at: SHELL, from: -40, to: -22, height: TALL }, -31, 4.4, 3.6),

  // --- Shop backs / service corridor inner wall --------------------------
  // Punched with a service door behind every unshuttered unit, on both levels,
  // which is the whole back-of-house route. The door positions come from the unit
  // widths rather than from hand-picked numbers: the four doors this wall used to
  // have lined up with no unit centre at all, so every one of them opened onto the
  // solid back of a shop.
  ...serviceWall(-BACK, [
    { y: 0, top: L1, doors: doorsFor(L0_FROM, westL0) },
    { y: L1, top: TALL, doors: doorsFor(L1_FROM, westL1) },
  ]),
  ...serviceWall(BACK, [
    { y: 0, top: L1, doors: doorsFor(L0_FROM, eastL0) },
    { y: L1, top: TALL, doors: doorsFor(L1_FROM, eastL1) },
  ]),

  // --- Level 1 gallery edge ----------------------------------------------
  // Collider only; the railing prop is the thing you actually see.
  { axis: 'z', at: -7, from: Z_N, to: -3, y: L1, height: 1.1, invisible: true },
  { axis: 'z', at: -7, from: 5, to: Z_S, y: L1, height: 1.1, invisible: true },
  { axis: 'z', at: 7, from: Z_N, to: -3, y: L1, height: 1.1, invisible: true },
  { axis: 'z', at: 7, from: 5, to: Z_S, y: L1, height: 1.1, invisible: true },
  { axis: 'x', at: -3, from: -7, to: 7, y: L1, height: 1.1, invisible: true },
  // The north edge is where the two escalators arrive, so it is NOT continuous.
  // It was, and the whole upper floor was sealed off: both flights climbed five
  // metres and stopped dead against a balustrade. Nothing about that is visible
  // in a screenshot, and the level looked finished for weeks. The gaps are two
  // metres on the escalator centre lines, which is what a real one gets.
  { axis: 'x', at: 5, from: -7, to: -4.6, y: L1, height: 1.1, invisible: true },
  { axis: 'x', at: 5, from: -2.6, to: 2.6, y: L1, height: 1.1, invisible: true },
  { axis: 'x', at: 5, from: 4.6, to: 7, y: L1, height: 1.1, invisible: true },
  // The balcony over the food court.
  { axis: 'x', at: Z_N - 5, from: -BACK, to: BACK, y: L1, height: 1.1, invisible: true },

  // --- Food court --------------------------------------------------------
  // Side walls, with a service door each side into the corridor. The doors sit
  // at the far end rather than mid-wall because the middle twenty metres now
  // carry the food counters (see the `fc-west` / `fc-east` runs) and a service
  // door behind a shopfront is a door into the back of a fryer.
  ...withDoor({ axis: 'z', at: -BACK, from: Z_FC, to: Z_N, height: TALL }, -64, 1.2, 2.2),
  ...withDoor({ axis: 'z', at: BACK, from: Z_FC, to: Z_N, height: TALL }, -64, 1.2, 2.2),
  // Where the shop band stops and the food court begins.
  { axis: 'x', at: Z_N, from: -BACK, to: -ATRIUM, height: TALL },
  { axis: 'x', at: Z_N, from: ATRIUM, to: BACK, height: TALL },
  // Bulkhead over the atrium's opening into the food court — a low soffit makes
  // the double-height space beyond hit harder when you step through.
  { axis: 'x', at: Z_N, from: -ATRIUM, to: ATRIUM, y: 4.6, height: 0.9, ghost: true },

  // --- Control room ------------------------------------------------------
  { axis: 'x', at: CTRL_Z0, from: CTRL_X, to: -SHELL, height: 3.0, surface: 'concreteDark' },
  { axis: 'x', at: CTRL_Z1, from: CTRL_X, to: -SHELL, height: 3.0, surface: 'concreteDark' },
  { axis: 'z', at: CTRL_X, from: CTRL_Z0, to: CTRL_Z1, height: 3.0, surface: 'concreteDark' },

  // --- Loading dock ------------------------------------------------------
  { axis: 'x', at: -40, from: SHELL, to: 39, height: 6.0, surface: 'concreteDark' },
  { axis: 'x', at: -22, from: SHELL, to: 39, height: 6.0, surface: 'concreteDark' },
  // The roller door out to the yard. Never opens — it is scenery that explains
  // where the money goes.
  { axis: 'z', at: 39, from: -40, to: -22, height: 6.0, surface: 'concreteDark' },

  // --- Plaza -------------------------------------------------------------
  // These are now internal: they separate the entrance forecourt from the two
  // side lots rather than being the edge of the world. They stop short of z=39
  // so the forecourt opens straight onto the apron.
  { axis: 'z', at: -17, from: Z_S, to: 36, height: 3.4, surface: 'concrete' },
  { axis: 'z', at: 17, from: Z_S, to: 36, height: 3.4, surface: 'concrete' },

  /**
   * --- The south elevation --------------------------------------------------
   *
   * Fifty metres wide, twelve high, and until now one unbroken plane of
   * plaster — the first thing anybody sees of this building and the flattest
   * surface in it. Nothing here changes the wall; it is all relief stuck onto
   * the front of it, which is exactly how the real thing is built.
   *
   * A fascia band at nine and a half metres cuts the height into a two-thirds
   * and a one-third, and the pilasters break the width into bays. Between them
   * the wall stops being fifty metres of anything and becomes six panels, which
   * is the difference between a facade and a backdrop.
   */
  { axis: 'x', at: 24.32, from: -SHELL, to: SHELL, y: 9.5, height: 0.65, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: 24.32, from: -SHELL, to: SHELL, y: 10.15, height: 0.22, surface: 'concrete', ghost: true },
  // A plinth course at the bottom, so the wall meets the tarmac on a line
  // rather than just stopping.
  { axis: 'x', at: 24.34, from: -SHELL, to: SHELL, y: 0, height: 0.55, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: 24.5, from: -SHELL, to: -11.4, y: 5.0, height: 0.3, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: 24.5, from: 11.4, to: SHELL, y: 5.0, height: 0.3, surface: 'concreteDark', ghost: true },
  ...([-22.2, -17.4, -12.4, 12.4, 17.4, 22.2].map((x) => ({
    axis: 'z' as const,
    at: x,
    from: 24.2,
    to: 24.62,
    y: 0,
    height: 10.4,
    thickness: 0.85,
    surface: 'plaster' as const,
    ghost: true,
  }))),

  /**
   * --- The parapet ---------------------------------------------------------
   *
   * Ghost, because nothing walks up here — but geometry, because the entire
   * point of a parapet is the line it draws against the sky. A flat roof that
   * ends exactly where its wall ends reads as a lid dropped onto a box; a wall
   * that carries a metre past the deck reads as a building. It is one of the
   * cheapest silhouette wins available and the mall had none of it.
   */
  { axis: 'z', at: -SHELL, from: Z_FC, to: Z_S, y: CANOPY, height: 1.3, surface: 'concreteDark', ghost: true },
  { axis: 'z', at: SHELL, from: Z_FC, to: Z_S, y: CANOPY, height: 1.3, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: Z_S, from: -SHELL, to: SHELL, y: CANOPY, height: 1.3, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: Z_FC, from: -SHELL, to: -BACK, y: CANOPY, height: 1.3, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: Z_FC, from: BACK, to: SHELL, y: CANOPY, height: 1.3, surface: 'concreteDark', ghost: true },
  // Upstand around the two light wells, so the atrium glazing sits in a kerb
  // rather than being a hole cut in a plane.
  { axis: 'z', at: -ATRIUM, from: Z_N, to: Z_S, y: CANOPY, height: 0.7, surface: 'concreteDark', ghost: true },
  { axis: 'z', at: ATRIUM, from: Z_N, to: Z_S, y: CANOPY, height: 0.7, surface: 'concreteDark', ghost: true },
  { axis: 'x', at: Z_N, from: -ATRIUM, to: ATRIUM, y: CANOPY, height: 0.7, surface: 'concreteDark', ghost: true },
  { axis: 'z', at: -BACK, from: Z_FC, to: Z_N, y: CANOPY, height: 0.7, surface: 'concreteDark', ghost: true },
  { axis: 'z', at: BACK, from: Z_FC, to: Z_N, y: CANOPY, height: 0.7, surface: 'concreteDark', ghost: true },

  /**
   * --- The edge of the world ------------------------------------------------
   *
   * Colliders only. The fence that stands here is a prop, because a chain-link
   * panel has to be an alpha-cut plane you can see the horizon through, and the
   * wall builder makes solid boxes. Splitting them means the barrier can be
   * transparent and still be a barrier.
   *
   * Two and a half metres, not the fence's 2.4 — a hair over, so there is no
   * geometry a player can get their head above.
   */
  { axis: 'x', at: 64, from: -34, to: 34, height: 2.5, invisible: true },
  { axis: 'z', at: -34, from: Z_S, to: 64, height: 2.5, invisible: true },
  { axis: 'z', at: 34, from: Z_S, to: 64, height: 2.5, invisible: true },
  // The returns beside the building: the side lots run wider than the shell, so
  // without these you could walk north up the flank and off the map.
  { axis: 'x', at: Z_S, from: -34, to: -SHELL, height: 2.5, invisible: true },
  { axis: 'x', at: Z_S, from: SHELL, to: 34, height: 2.5, invisible: true },
];

/** The wall behind a shop run, with service doors punched at given z positions. */
/**
 * The wall between the shop band and a service corridor, in horizontal bands.
 *
 * One band per floor, because the two floors' units are different widths and so
 * their doors are in different places. A single full-height wall cannot have two
 * sets of holes in it.
 */
function serviceWall(
  x: number,
  bands: Array<{ y: number; top: number; doors: number[] }>,
): WallDef[] {
  const out: WallDef[] = [];
  const half = BACK_DOOR_W / 2 + 0.05;
  for (const band of bands) {
    const height = band.top - band.y;
    let cursor = Z_N;
    for (const d of [...band.doors].sort((a, b) => a - b)) {
      out.push({ axis: 'z', at: x, from: cursor, to: d - half, y: band.y, height });
      // Lintel over the door: the wall above head height carries straight on.
      out.push({
        axis: 'z',
        at: x,
        from: d - half,
        to: d + half,
        y: band.y + BACK_DOOR_H,
        height: height - BACK_DOOR_H,
      });
      cursor = d + half;
    }
    out.push({ axis: 'z', at: x, from: cursor, to: Z_S, height, y: band.y });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

const runs: MallPlan['runs'] = [
  {
    id: 'west-l0',
    axis: 'z',
    at: -ATRIUM,
    from: L0_FROM,
    facing: 1,
    y: 0,
    height: H0,
    depth: DEPTH,
    gap: UNIT_GAP,
    units: westL0,
    backDoor: true,
  },
  {
    id: 'east-l0',
    axis: 'z',
    at: ATRIUM,
    from: L0_FROM,
    facing: -1,
    y: 0,
    height: H0,
    depth: DEPTH,
    gap: UNIT_GAP,
    units: eastL0,
    backDoor: true,
  },
  {
    id: 'west-l1',
    axis: 'z',
    at: -ATRIUM,
    from: L1_FROM,
    facing: 1,
    y: L1,
    height: H1,
    depth: DEPTH,
    gap: UNIT_GAP,
    units: westL1,
    backDoor: true,
  },
  {
    id: 'east-l1',
    axis: 'z',
    at: ATRIUM,
    from: L1_FROM,
    facing: -1,
    y: L1,
    height: H1,
    depth: DEPTH,
    gap: UNIT_GAP,
    units: eastL1,
    backDoor: true,
  },
  // The food court counter, across the far end.
  {
    id: 'foodcourt',
    axis: 'x',
    at: -62,
    from: -8,
    facing: 1,
    y: 0,
    height: 4.4,
    depth: 6,
    units: [{ store: 'foodcourt', width: 16 }],
  },
  /**
   * The rest of the food court, down both flanks.
   *
   * One lit counter facing forty metres of blank side wall is a diorama, not a
   * food court. Reference panel 8 is a horseshoe of units with most of them
   * shuttered — the emptiness only reads if there is a row of places that used
   * to be busy. Four metres deep against the |x| = 21 walls leaves the middle
   * thirty metres for seating, which is the correct proportion and also the
   * reason the seating grid stopped looking like it was floating in a hangar.
   *
   * Two of the six are lit. Any fewer and the flanks are still dark; any more
   * and the main counter stops being the thing you walk toward.
   */
  {
    id: 'fc-west',
    axis: 'z',
    at: -17,
    from: -60,
    facing: 1,
    y: 0,
    height: 4.4,
    depth: 4,
    gap: 0.6,
    units: [
      vacant(4.6, 'WOK THIS WAY'),
      vacant(4.6, 'THE POTATO CONCEPT', true),
      vacant(4.6, 'MEAT & GREET'),
    ],
  },
  {
    id: 'fc-east',
    axis: 'z',
    at: 17,
    from: -60,
    facing: -1,
    y: 0,
    height: 4.4,
    depth: 4,
    gap: 0.6,
    units: [
      vacant(4.6, 'SUB CULTURE', true),
      vacant(4.6, 'FROZEN ASSETS'),
      vacant(4.6, 'NOODLE ADJACENT'),
    ],
  },
];

// ---------------------------------------------------------------------------
// Vertical circulation
// ---------------------------------------------------------------------------

const stairs: StairDef[] = [
  // Escalator pair into the middle of the atrium, landing on the bridge.
  { x: -3.6, z: 13.2, y: 0, rise: L1, run: 8.2, width: 1.2, yaw: Math.PI, build: escalator },
  { x: 3.6, z: 13.2, y: 0, rise: L1, run: 8.2, width: 1.2, yaw: Math.PI, build: escalator },
  // Concrete stair in each service corridor — the unglamorous way up, and the
  // only route between levels that no camera watches.
  { x: -22.75, z: -14, y: 0, rise: L1, run: 6.4, width: 1.6, yaw: 0, build: stairFlight },
  { x: 22.75, z: 6, y: 0, rise: L1, run: 6.4, width: 1.6, yaw: Math.PI, build: stairFlight },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

const props: PropDef[] = [];

// `Object3D`, not `Group`: most props are groups, but some — a light wash, a
// single sign quad — are one mesh, and there is nothing to be gained by making
// the caller wrap those in an empty group to satisfy a type.
function prop(x: number, z: number, build: () => Object3D, opts: Partial<PropDef> = {}) {
  props.push({ x, z, build, ...opts });
}

// Atrium spine: planters, benches, and the directory. Reference panel 5 puts
// fake foliage every twenty metres and nothing else in the middle.
for (const z of [-34, -20, 8, 20]) {
  prop(0, z, () => planter(1.5), { collide: { hx: 1.5, hy: 0.3, hz: 1.5 } });
}

/**
 * The dry fountain, dead centre of the atrium, where a planter used to be.
 *
 * The spine was five identical planters twenty metres apart, which is a
 * perfectly accurate mall and a completely inert one — you could stand at any
 * point in a ninety-metre concourse and the view north and the view south were
 * the same view. This gives the middle of the building one object it is *about*,
 * and it is an object whose entire content is absence: a fountain in working
 * order, uplighters running, no water in it.
 */
prop(0, -6, () => drainedFountain(3.2), { collide: { hx: 3.3, hy: 0.35, hz: 3.3 } });

// Atrium night lighting, recessed under the level-1 gallery edge. Without these
// the only light reaching the middle of the building is moonlight through the
// canopy, and every vertical surface — escalators, planters, mannequins, the
// player — reads as a flat black cut-out. Ten lights for the whole concourse;
// they are deliberately far apart so the dark between them is still dark.
for (const z of [-36, -22, -8, 6, 18]) {
  for (const side of [-1, 1]) {
    prop(side * 9.6, z, () => downlight(L1 - 0.45, 1));
  }
}

/**
 * The same run again, one floor up.
 *
 * Level two had no light of its own at all. Standing in the atrium it was a
 * black band with a row of orange sign strips floating in it — the building
 * appeared to have one storey and a shelf, and you could not tell whether the
 * gallery was somewhere you were allowed to go. That reads as unfinished more
 * than any single missing prop does, because a whole floor of a real building
 * is never simply off.
 *
 * Dimmer than the pair below and offset half a bay from them, so the two levels
 * do not stripe in register and the gallery keeps long dark stretches to move
 * through.
 */
for (const z of [-29, -15, -1, 12, 22]) {
  for (const side of [-1, 1]) {
    prop(side * 10.2, z, () => downlight(L1 + H1 - 0.5, 0.62), { y: L1 });
  }
}
prop(-4.5, 21, () => directoryPylon('DIRECTORY\n\nLEVEL 1  RETAIL\nLEVEL 2  RETAIL\n\nYOU ARE HERE'), {
  yaw: Math.PI,
  collide: { hx: 0.5, hy: 1.0, hz: 0.15 },
});
prop(4.5, 21, () => directoryPylon('NOTICE\n\nTHIS CENTRE IS\nMONITORED AT\nALL TIMES\n\nENJOY YOUR VISIT'), {
  yaw: Math.PI,
  collide: { hx: 0.5, hy: 1.0, hz: 0.15 },
});

// Mannequins. Never in a shop window where you would expect them — always out on
// the concourse, facing slightly the wrong way. They are the only figures in the
// building and their entire job is to make you look twice.
const MANNEQUINS: Array<[number, number, number, 'stand' | 'contrapposto' | 'armsOut']> = [
  [-9.4, -28, 0.4, 'contrapposto'],
  [-9.4, -26.2, 2.9, 'stand'],
  [9.6, -12, 3.9, 'armsOut'],
  [10.2, 4, 1.2, 'stand'],
  [-10.6, 15, 2.2, 'contrapposto'],
  [6.2, -41, 0.1, 'armsOut'],
];
for (const [x, z, yaw, pose] of MANNEQUINS) {
  prop(x, z, () => mannequin(pose), { yaw, collide: { hx: 0.3, hy: 0.9, hz: 0.3 } });
}

// Gallery railings — the visible half of the invisible walls above.
for (const side of [-1, 1]) {
  for (const [z0, z1] of [
    [Z_N, -3],
    [5, Z_S],
  ]) {
    const len = z1 - z0;
    prop(side * 7, (z0 + z1) / 2, () => railing(len), { y: L1, yaw: Math.PI / 2 });
  }
}
prop(0, Z_N - 5, () => railing(BACK * 2), { y: L1 });
for (const x of [-4, 4]) prop(x, -3, () => railing(6), { y: L1 });
// North edge: the two escalator mouths are left open, so the rail is three
// pieces and the visible geometry agrees with the colliders above.
for (const [x, len] of [
  [-5.8, 2.4],
  [0, 5.2],
  [5.8, 2.4],
] as const) {
  prop(x, 5, () => railing(len), { y: L1 });
}

// --- Food court -----------------------------------------------------------
// Panel 8: the chairs are still out, the lights are off.
//
// Narrowed from a six-wide grid to five and pulled inboard, because the flanks
// are now counters rather than blank wall and tables were sitting where the
// queue would be. The jitter is what stops twenty identical tables reading as
// an array — they are pushed around by however many people last sat at them.
for (let i = 0; i < 20; i++) {
  const gx = (i % 5) - 2;
  const gz = Math.floor(i / 5);
  const wobble = ((i * 97) % 13) / 13 - 0.5;
  prop(gx * 5.4 + wobble * 1.4, -47.5 - gz * 4.4 + wobble * 1.1, () => cafeTable(), {
    yaw: ((i * 37) % 360) * (Math.PI / 180),
    collide: { hx: 0.7, hy: 0.4, hz: 0.7 },
  });
}

/**
 * Closing time, frozen.
 *
 * The stacks are the point: chairs stacked against a wall are the one prop that
 * says the staff got most of the way through shutting up and then stopped, and
 * they are what makes the tables still out on the floor read as unfinished
 * business rather than as level dressing nobody thought about.
 */
for (const [x, z, n] of [
  [-14.2, -63.4, 6],
  [-13.4, -62.2, 4],
  [13.8, -63.8, 5],
  [15.0, -62.6, 7],
] as const) {
  prop(x, z, () => chairStack(n), {
    yaw: ((x * 13) % 6) * 0.2,
    collide: { hx: 0.25, hy: 0.6, hz: 0.25 },
  });
}
prop(11.4, -58.6, () => mopBucket());
prop(-11.8, -45.6, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
prop(11.8, -45.6, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
prop(-9.4, -60.8, () => planter(1.3), { collide: { hx: 1.3, hy: 0.3, hz: 1.3 } });
prop(9.4, -60.8, () => planter(1.3), { collide: { hx: 1.3, hy: 0.3, hz: 1.3 } });

// Menu boards over the counters. Placed on the fascia of each flanking unit at
// eye-line-plus, which is the height that breaks up a 4.4m shopfront; two of
// the six are dark, matching the two units whose signage is still lit.
for (const [side, sign] of [[-1, 1], [1, -1]] as const) {
  for (const [i, z] of [-57.4, -52.2, -47.0].entries()) {
    const west = side < 0;
    prop(side * 16.85, z, () => menuBoard(3.0, 0.75, west
      ? ['ALL DAY BREAKFAST\n£4.20', 'CHIPS  ANY SIZE\nONE PRICE', 'SET MEAL  A / B / C'][i]
      : ['SUB OF THE DAY\nASK', 'NOW SERVING\n— ', 'BOWLS  FROM £5'][i],
      i === (west ? 1 : 0)), { y: 2.9, yaw: sign * Math.PI / 2 });
  }
}

/**
 * Light on the upper half of the room, which had none.
 *
 * The food court is ten and a half metres to the glazing and everything that
 * lit it was at counter height, so above four metres the frame was a black
 * band with a skylight grid floating in it. These are washes, not lights: the
 * chairs and tables already read fine off the counters' spill, and what is
 * missing is the architecture, which is exactly the job a wash does. See the
 * service corridor for the same three-part remedy.
 */
for (const side of [-1, 1]) {
  for (const z of [-64, -56, -48]) {
    prop(side * 20.83, z, () => lightWash(11, 9.0, EMIT.shopWarm, 0.1, 'fc-wall'), {
      yaw: side > 0 ? -Math.PI / 2 : Math.PI / 2,
      y: 6.8,
    });
  }
}
// And the back wall above the main counter, which is the shot's vanishing point.
prop(0, -67.83, () => lightWash(26, 10.0, EMIT.shopWarm, 0.1, 'fc-back'), { yaw: 0, y: 6.6 });

/**
 * Four pendants slung into the middle of the volume.
 *
 * The washes give the upper walls a gradient but they cannot put an OBJECT up
 * there, and a ten-metre room with nothing between the tables and the glazing
 * still reads as a hangar with furniture in it. Hung at six metres these break
 * the empty band, they light the seating from above instead of from the
 * counters, and the two carrying real lights are what finally give the chairs
 * form rather than silhouette. See the control room for why the other two do
 * not: a real light is a global cost paid by every lit fragment in the mall.
 */
for (const [x, z, real] of [
  [-8.5, -50.5, true],
  [8.5, -50.5, false],
  [-8.5, -60.5, false],
  [8.5, -60.5, true],
] as const) {
  prop(x, z, () => pendantLamp(10.5, 6.0, { realLight: real, intensity: real ? 1 : 0.8 }));
}

// Vending machines: light sources in dark places, and cover.
for (const [x, z, yaw] of [
  [-22.9, -30, Math.PI / 2],
  [22.9, -8, -Math.PI / 2],
  [-12.6, -44.6, 0],
  [19.5, -46, Math.PI],
] as const) {
  prop(x, z, () => vendingMachine(), { yaw, collide: { hx: 0.55, hy: 0.95, hz: 0.4 } });
}

// Service corridor lighting and ductwork — the panel-4 look. Every third fitting
// is dead, which is what makes the live ones feel like a decision.
for (let z = Z_FC + 4; z < Z_S; z += 6) {
  for (const side of [-1, 1]) {
    const dead = Math.abs(Math.round(z / 6)) % 3 === 0;
    prop(side * 22.75, z, () => fluorescentPanel(0.7, 1.6, { ceilingY: SVC_CEIL, intensity: dead ? 0 : 0.9 }));
    if (z > Z_N) {
      prop(side * 22.75, z, () => fluorescentPanel(0.7, 1.6, { ceilingY: L1 + SVC_CEIL, intensity: dead ? 0.9 : 0.4 }), { y: L1 });
    }

    /**
     * Glow on both corridor walls, and on the ceiling around the fitting.
     *
     * Without these the corridor measured 79% pure black: the fittings are
     * emissive quads and their pools are floor quads, so between them they lit
     * the ground and nothing above it, and the walls and ceiling of a 3.5m-wide
     * tube simply were not there. A real light per fitting is the correct fix
     * and costs twenty-odd forward-rendered lights the mall has no budget for.
     * See `lightWash`.
     *
     * Dead fittings still get a faint wash rather than none, because a gap in a
     * corridor is lit by its neighbours — cutting to absolute black between
     * fittings gives the wall a striped, decal-like look instead of a fall-off.
     * The quads are sized to the 6m fitting pitch for the same reason: they have
     * to overlap into each other or the seams show.
     */
    const strength = dead ? 0.05 : 0.15;
    for (const inner of [true, false]) {
      // The corridor runs between |x| = BACK and |x| = SHELL, so the two faces it
      // has are the OUTSIDE of the shop backs and the INSIDE of the outer shell —
      // which means the two washes sit just off those planes pointing at each
      // other, not both pointing the same way.
      // Walls are 0.3 thick and centred on their line, so the corridor face is a
      // clear 0.15 out from `at` — a 0.06 offset buries the quad inside the wall
      // and it vanishes, which is exactly what happened on the first attempt.
      const x = side * (inner ? BACK + 0.17 : SHELL - 0.17);
      const yaw = side * (inner ? Math.PI / 2 : -Math.PI / 2);
      prop(x, z, () => lightWash(9, 3.4, EMIT.fluoro, strength, 'svc-wall'), { yaw, y: 2.4 });
    }
    // And one flat against the ceiling. A troffer recessed in a grid ceiling
    // spills onto the tiles around it; without this the top third of every frame
    // in here is a black band and the corridor reads as roofless.
    prop(
      side * 22.75,
      z,
      () => {
        // The wash is built in the XY plane facing +z; tipping it a quarter turn
        // about x points it straight down and lays its long axis along the run.
        const w = lightWash(3.4, 6.5, EMIT.fluoro, strength * 3.0, 'svc-ceil');
        w.rotation.x = Math.PI / 2;
        return w;
      },
      { y: SVC_CEIL - 0.14 },
    );
  }
}

/**
 * Corridor dressing. A service corridor with nothing in it is just a hallway,
 * and the whole point of the back of house is that it is where the mall keeps
 * the things it does not want seen — so it gets the stock cages, pallets and
 * bin store that make the shops in front of it feel like a facade.
 *
 * Alternating sides and irregular spacing on purpose: evenly placed props down
 * a straight corridor read as wallpaper rather than as clutter.
 */
const SVC_CLUTTER = [-62, -55, -50, -43, -39, -33, -27, -21, -16, -9, -3, 3, 9, 16, 21];
/**
 * Doorways in the outer shell — the wall the clutter parks against. Side, centre
 * and half-width.
 *
 * A litter bin standing dead centre in the control room's 1.2m door sealed the
 * guards' own spawn room off from the entire rest of the building. Nobody could
 * see it: the bin looks like set dressing, the door looks like a door, and it
 * took a flood fill over the navigation grid to notice that the room had no
 * exit. Anything parked against the shell has to know where the holes in it are.
 */
const SHELL_DOORS: Array<[number, number, number]> = [
  [-1, -3, 0.6],
  [1, -31, 2.2],
];
for (const side of [-1, 1]) {
  for (const [i, z] of SVC_CLUTTER.entries()) {
    if (SHELL_DOORS.some(([s, at, half]) => s === side && Math.abs(z - at) < half + 1.2)) continue;
    // Skip roughly a third, offset per side, so the two corridors do not mirror
    // each other and neither reads as an evenly spaced row.
    if ((i + (side > 0 ? 2 : 0)) % 3 === 2) continue;
    // Against the shell, and clear of it by half the prop's own depth — parked
    // flush at SHELL - 0.55 puts a 1.2m-deep cage half inside the wall.
    const against = side * (SHELL - 0.95);
    const kind = (i * 5 + (side > 0 ? 3 : 0)) % 3;
    if (kind === 0) {
      prop(against, z, () => stockCage(1.0, 1.9, 1.2), {
        yaw: side > 0 ? Math.PI : 0,
        collide: { hx: 0.5, hy: 0.95, hz: 0.6 },
      });
    } else if (kind === 1) {
      prop(against, z, () => palletStack(0.8 + (i % 3) * 0.3), {
        collide: { hx: 0.6, hy: 0.5, hz: 0.5 },
      });
    } else {
      prop(against, z, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
    }
  }
}
for (const side of [-1, 1]) {
  prop(side * 23.6, (Z_FC + Z_S) / 2, () => ductRun(Z_S - Z_FC, SVC_CEIL - 0.55), { yaw: Math.PI / 2 });
}

// Exit signage. Every one of them points somewhere real.
for (const [x, z, y, yaw] of [
  [0, Z_S - 0.6, 3.2, 0],
  [-21.4, -6, 2.5, Math.PI / 2],
  [21.4, 2, 2.5, -Math.PI / 2],
  [-21.4, -58, 2.5, Math.PI / 2],
  [0, Z_N + 0.5, 4.3, Math.PI],
] as const) {
  prop(x, z, () => exitSign(), { y, yaw });
}

// --- Loading dock ---------------------------------------------------------
// Reference panel 7. Sodium, concrete, and the only room in the building that
// admits the mall is a machine for moving boxes.
//
// The middle lamp carries a real light and the other two do not. The dock is
// eighteen metres of bare slab and everything in it was silhouetting against
// its own floor pool; one light over the platform is what makes the skip, the
// cages and the dock's own handles have form. Same lesson as the control room.
for (const [z, real] of [[-36, false], [-30, true], [-25, false]] as const) {
  prop(31, z, () => sodiumLamp({ ceilingY: 5.6, realLight: real }));
}
prop(38.8, -31, () => rollerDoor(6.0, 4.6, 0), { yaw: -Math.PI / 2 });
// Bumpers either side of the door, at trailer bed height.
for (const z of [-34.4, -27.6]) {
  prop(38.6, z, () => dockBumper(), { y: 0.95, yaw: -Math.PI / 2 });
}

/**
 * Six metres of ceiling and eighteen of wall, none of which existed.
 *
 * The dock measured 40% black with the entire left half of frame at zero: the
 * three pendants lit their own floor pools and nothing else, so the slab
 * overhead, the north wall and the shell wall behind the camera were all
 * absent. Same three-part remedy as the service corridor — a ceiling wash per
 * fitting so the lid exists, a wall wash so the box has sides, and enough
 * clutter that the space reads as used rather than unbuilt.
 */
for (const z of [-36, -30, -25]) {
  prop(31, z, () => {
    const w = lightWash(7.5, 9.0, EMIT.sodium, 0.16, 'dock-ceil');
    w.rotation.x = Math.PI / 2;
    return w;
  }, { y: 5.86 });
}
// Long washes down both side walls. Clear of the 0.3-thick wall solids by 0.17,
// the same clearance the corridor needed — at 0.06 they render inside the wall
// and do nothing at all.
for (const [z, yaw] of [[-39.83, 0], [-22.17, Math.PI]] as const) {
  prop(31, z, () => lightWash(15, 5.5, EMIT.sodium, 0.11, 'dock-side'), { yaw, y: 2.6 });
}
prop(38.83, -31, () => lightWash(13, 5.5, EMIT.sodium, 0.13, 'dock-end'), {
  yaw: -Math.PI / 2,
  y: 2.8,
});

// Back-of-house clutter. The skip is the tall silhouette that breaks the roller
// door's rectangle; everything else is scale reference.
//
// Everything sits at least eight metres off the review camera. Parked closer,
// the skip alone filled the top-left corner as an unreadable brown slab with
// its own stencil cropped by the frame edge — a foreground object has to be far
// enough away to be recognised as the thing it is.
prop(28.6, -38.2, () => dumpster(2.4, 1.5, 1.3), {
  yaw: 0.14,
  collide: { hx: 1.2, hy: 0.75, hz: 0.7 },
});
prop(34.6, -38.6, () => stockCage(1.0, 1.9, 1.2), {
  yaw: -0.3,
  collide: { hx: 0.5, hy: 0.95, hz: 0.6 },
});
prop(36.4, -37.2, () => stockCage(1.0, 1.9, 1.2), {
  yaw: 0.22,
  collide: { hx: 0.5, hy: 0.95, hz: 0.6 },
});
for (const [x, z, h] of [[25.2, -36.4, 0.9], [25.8, -34.8, 1.35], [36.9, -24.4, 1.1]] as const) {
  prop(x, z, () => palletStack(h), { collide: { hx: 0.6, hy: h / 2, hz: 0.5 } });
}
prop(25.0, -27.4, () => handTruck(), { yaw: 1.2 });
prop(34.2, -23.2, () => mopBucket());
prop(28.4, -22.24, () => electricalPanel(0.7, 0.9), { y: 1.6, yaw: Math.PI });
prop(24.4, -28.2, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
prop(38.7, -23.6, () => exitSign(), { y: 2.6, yaw: -Math.PI / 2 });
// Ductwork across the ceiling, so the six-metre slab has something crossing it.
prop(29.5, -31, () => ductRun(15, 5.1), { yaw: Math.PI / 2 });

// Control room. Panel 9 exactly: monitor wall, desk, radios, one dead fitting.
// Kept tight — the guard's whole world is two metres of desk and a wall of other
// people's rooms.
const CTRL_MID = (CTRL_Z0 + CTRL_Z1) / 2;
prop(CTRL_X + 0.1, CTRL_MID, () => monitorWall(5, 3), { y: 1.45, yaw: Math.PI / 2 });
prop(CTRL_X + 2.3, CTRL_MID, () => controlDesk(3.0), {
  yaw: Math.PI / 2,
  collide: { hx: 0.45, hy: 0.4, hz: 1.5 },
});
// On the desk, angled at the chair. See `chuteConsole` — this is the guard's
// only control over the thieves' drop-offs and it has to be findable from the
// door, so it sits at the end of the desk nearest whoever walks in.
prop(CTRL_X + 2.3, CTRL_MID - 0.95, () => chuteConsole(1.4, 6), { y: 0.82, yaw: Math.PI / 2 });
prop(CTRL_X + 2.35, CTRL_MID + 1.05, () => deskLamp(), { y: 0.82, yaw: Math.PI / 2 + 0.4 });
prop(-24.9, CTRL_Z1 - 1.4, () => radioCharger(), { y: 0.95, yaw: -Math.PI / 2 });
prop(CTRL_X + 0.13, CTRL_Z0 + 0.9, () => keyCabinet(), { y: 1.5, yaw: Math.PI / 2 });
prop(-24.62, CTRL_Z0 + 1.9, () => pinBoard(1.0, 0.7), { y: 1.75, yaw: -Math.PI / 2 });
// The one real light in the room, and the only one it needs.
//
// Everything else in here is emissive quads and additive washes, and neither of
// those illuminates anything — which is why the desk, the chair, the console and
// the rack were all pure silhouettes in a room that measured as half lit. A wash
// makes the surface it is painted on brighter; it does not put light on the
// objects standing in front of it. Twenty-six of these down the service corridor
// was unaffordable. One over the guard's desk is not.
prop(CTRL_X + 2.4, CTRL_Z0 + 1.0, () =>
  fluorescentPanel(0.8, 1.8, { ceilingY: 3.0, intensity: 0.5, realLight: true }),
);
prop(-26.4, CTRL_Z1 - 1.2, () => fluorescentPanel(0.8, 1.8, { ceilingY: 3.0, intensity: 0 }));

/**
 * The room's own light, which it did not previously have any of.
 *
 * Measured at 56% pure black: the monitor wall threw a green puddle on the floor
 * and the ceiling fitting threw another, and between them nothing touched a
 * vertical surface or the slab overhead — so fifteen screens and a fluorescent
 * tube floated in a void. The monitors are the room's main source and they face
 * the desk, so the wash that sells that is on the wall BEHIND the desk, not
 * behind the screens.
 */
for (const [w, d, i, z] of [
  // A broad, weak lift over the whole slab so the ceiling exists at all, then a
  // tight bright one at the fitting so it still has a source. One wash cannot do
  // both: sized to the room it is a flat grey lid, sized to the fitting it is a
  // spotlight decal on black.
  [7.0, 8.0, 0.09, CTRL_MID],
  [2.4, 3.2, 0.34, CTRL_Z0 + 1.0],
] as const) {
  prop(CTRL_X + 2.42, z, () => {
    const wash = lightWash(w, d, EMIT.fluoro, i, 'ctrl-ceil');
    wash.rotation.x = Math.PI / 2;
    return wash;
  }, { y: 2.86 });
}
prop(-24.63, CTRL_MID, () => lightWash(5.2, 2.8, EMIT.crt, 0.3, 'ctrl-back'), {
  yaw: -Math.PI / 2,
  y: 1.6,
});
for (const z of [CTRL_Z0 + 0.02, CTRL_Z1 - 0.02]) {
  prop(CTRL_X + 2.6, z, () => lightWash(5.0, 3.0, EMIT.fluoro, 0.3, 'ctrl-side'), {
    yaw: z < CTRL_MID ? 0 : Math.PI,
    y: 1.7,
  });
}
// Green spill on the wall the screens are mounted on. Fifteen tubes bright
// enough to light a room from four metres also light the plaster around them,
// and without it the bank floats in a void with a hard rectangular edge.
prop(CTRL_X + 0.22, CTRL_MID, () => lightWash(6.0, 3.0, EMIT.crt, 0.22, 'ctrl-screens'), {
  yaw: Math.PI / 2,
  y: 1.7,
});

/**
 * The far side of the room, which was eight square metres of empty concrete.
 *
 * A guard sat alone in a bare box has nothing to look at but the screens, and
 * the room stops being a place. The rack is also the honest answer to where
 * fifteen camera feeds go, and the lockers give the far wall a silhouette tall
 * enough to break the ceiling line.
 */
prop(CTRL_X + 0.55, CTRL_Z0 + 0.45, () => serverRack(1.9), {
  yaw: Math.PI / 2,
  collide: { hx: 0.36, hy: 0.95, hz: 0.31 },
});
prop(CTRL_X + 2.6, CTRL_Z0 + 0.28, () => lockerBank(4), {
  collide: { hx: 0.76, hy: 0.9, hz: 0.23 },
});
// A vending machine and a bin, because the room has to look occupied by someone
// who has been here a long time.
prop(-25.4, CTRL_Z0 + 1.1, () => vendingMachine(), {
  yaw: -Math.PI / 2,
  collide: { hx: 0.55, hy: 0.95, hz: 0.4 },
});
prop(CTRL_X + 1.0, CTRL_Z1 - 0.8, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });

// --- The plaza ------------------------------------------------------------
// Reference panel 2: one enormous piece of corporate typography over a lit
// aperture, and a forecourt bare enough that the doorway is the only event.
// z = 24.4, not 23.6: the plaza is on the +Z side of the south wall, so a sign
// authored a few centimetres the other way is mounted inside the atrium facing
// the wrong direction and invisible from the only place it matters.
prop(0, 24.4, () => facadeSign('MERIDIAN CROSSING', 15, 1.9), { y: 7.6 });
prop(0, 24.4, () => facadeSign('OPEN LATE', 5, 0.7, EMIT.signAmber), { y: 6.1 });

/**
 * The doors themselves, which the building did not previously have.
 *
 * The south wall is punched with a 9 × 4.6 opening (see the `withDoor` call at
 * the top of `walls`) and until now the approach shot framed the whole lit
 * atrium through a bare rectangular hole. `entranceDoors` fills it with a
 * glazed screen and props one leaf open — the aperture is the entire subject of
 * reference panel 2 and it has to have mullions for the brightness beyond it to
 * read as an interior rather than a light box.
 *
 * Ghosted: the wall it sits in already carries the doorway collider, and a
 * second one here would seal the only entrance in the building.
 */
prop(0, Z_S, () => entranceDoors(8.8, 4.6, 4), { yaw: Math.PI });

/**
 * Poster cases either side of the doors, and a camera over them.
 *
 * With the screen in, the only thing still wrong with the opening frame was the
 * plaster: two big fields of it flanking the doors with nothing on them at all.
 * These are the correct furniture for that wall and they carry the house voice
 * — the copy is what a shopping centre would genuinely have printed, which is
 * exactly why it lands. One of the four is dark, because one always is.
 */
for (const [x, copy, lit] of [
  [-7.4, 'MERIDIAN\nCROSSING\n\n— 84 STORES —\nUNDER ONE ROOF', true],
  [-5.6, 'THIS COULD\nBE YOUR\nADVERT\n\nENQUIRE WITHIN', false],
  [5.6, 'NOW OPEN\nLEVEL TWO\n\nA WHOLE NEW\nFLOOR OF\nPOSSIBILITY', true],
  [7.4, 'SMILE\n\nCCTV IN\nOPERATION\nFOR YOUR\nSAFETY', true],
] as const) {
  // z = Z_S + 0.28, on the PLAZA side. The south wall is 0.3 thick and centred
  // on its line, so anything authored at Z_S - anything is hung on the inside
  // face and is invisible from the only camera that cares. Same trap the façade
  // signs above already carry a comment about.
  prop(x, Z_S + 0.28, () => posterCase(1.4, 2.0, copy, lit), { y: 1.75 });
}
prop(3.4, Z_S + 0.32, () => securityCamera(), { y: 4.3, yaw: 0.5 });

/**
 * Paint on the forecourt, and two real lights over the door line.
 *
 * These are the two halves of the same fix. At 65% black the approach was the
 * worst frame in the game and both causes were on the ground: nothing drawn on
 * the tarmac, and nothing lighting it. The canopy runs from the south wall out
 * to z = 30, so a pair of downlights recessed into its soffit puts real
 * illumination on the doors, the bollards and the first six metres of asphalt —
 * which is exactly the band the player's opening frame is mostly made of.
 */
prop(0, 31.5, () => forecourtMarkings(30, 13.5));
/**
 * Two rows, not one, and the reason is the moon.
 *
 * The moon sits north-west at 33°, the mall is twelve metres tall and its south
 * wall is at z = 24, so the building throws an eighteen-metre shadow straight
 * down the forecourt and covers every inch of it. There is no camera angle and
 * no exposure setting that makes this car park moonlit — the only honest light
 * out here is the light the centre pays for. A single pair of downlights over
 * the door line left everything from the bollards outward unlit, which is the
 * half of the frame the player spawns standing in.
 *
 * A row at the canopy lip does the mid-ground, and the wide pair spreads the
 * near corners so the tarmac has a gradient across it rather than one bright
 * disc. 0.7 by the doors and 0.5 further out: at full drive the canopy soffit
 * floods and the whole façade goes warm tan, which is the one colour the
 * moonlit blue-grey palette does not have.
 */
for (const [x, z, drive] of [
  [-4.4, 26.6, 0.7],
  [4.4, 26.6, 0.7],
  [-8.8, 27.0, 0.5],
  [8.8, 27.0, 0.5],
  [-4.4, 29.3, 0.55],
  [4.4, 29.3, 0.55],
] as const) {
  // No fake pool out here. Every one of these has a real light behind it that
  // the bake resolves into the tarmac, and the decal was only ever a second,
  // brighter, square-edged copy of the same pool sitting on top of it.
  prop(x, z, () => downlight(5.5, drive, { pool: false }));
}
for (const x of [-6, -4.5, -3, 3, 4.5, 6]) {
  prop(x, 27.4, () => bollard(), { collide: { hx: 0.15, hy: 0.5, hz: 0.15 } });
}
/**
 * The façade reads as moonlit, the car park reads as sodium.
 *
 * It was doing neither. The south wall faces +z and the moon sits north-west, so
 * the front of the building is in the moon's shadow and the only light landing on
 * it was the two entrance lamp posts — with the result that eight metres of
 * plaster either side of the doors came out uniformly warm tan, which is the one
 * colour the reference sheet never uses. A real cool fill is the wrong tool: it
 * would cost forward-rendered light slots to paint a flat surface. The surface
 * *is* the subject here, so a wash is exactly right — it makes the plane exist
 * without lighting anything in front of it.
 */
for (const x of [-8.6, 8.6]) {
  prop(x, Z_S + 0.2, () => lightWash(13, 7.0, EMIT.moonlit, 0.09, 'facade'), { y: 3.4 });
}
/**
 * Column lighting across the whole apron, not a ring around the edge of it.
 *
 * Four columns used to stand out here and the middle of the car park measured
 * 6/255 — the tarmac between them was not dark, it was off, and the place the
 * entrance downlights gave out was a hard line drawn across the ground. Two
 * people have now reported that line as a bug and both were right.
 *
 * Warm belongs on the tarmac rather than on the building: a sodium head five
 * metres off the façade paints the plaster tan, which is the one colour the
 * moonlit palette does not have, so nothing sits closer to the wall than the
 * canopy lip. Beyond that the rule is spacing, not placement — ten metres
 * apart, pools overlapping, no column in the walk from the doors to the exit
 * and none inside the extraction disc. Six of them is what a real apron this
 * size carries.
 *
 * Every one of these is baked. The reach is free.
 */
for (const [x, z] of [
  [-14.5, 27.8],
  [14.5, 27.8],
  [-14.5, 34.8],
  [14.5, 34.8],
  // Flanking the extraction point, so the way out is the brightest thing on the
  // forecourt without needing a marker the fiction cannot explain.
  [-7.8, 37.4],
  [7.8, 37.4],
] as const) {
  prop(x, z, () => lampPost(5.4, { realLight: true, pool: false }), {
    collide: { hx: 0.2, hy: 2.7, hz: 0.2 },
  });
}

/**
 * Skylight over the forecourt, as four invisible sources rather than a term in
 * the ambient.
 *
 * Raising the global hemisphere would have lifted every unlit corridor in the
 * building by the same amount, and the dark interior is the game. These are
 * outside only, high enough that the shell shadows nothing from them, and they
 * carry no geometry — see LIGHTS.skyglow. They exist purely so the tarmac has a
 * cool floor under the sodium.
 */
for (const [x, z] of [
  [-11, 27],
  [11, 27],
  [-11, 36],
  [11, 36],
] as const) {
  prop(x, z, () => {
    const g = new Group();
    const l = new PointLight(
      LIGHTS.skyglow.color,
      LIGHTS.skyglow.intensity,
      LIGHTS.skyglow.distance,
      LIGHTS.skyglow.decay,
    );
    l.position.y = 17;
    g.add(l);
    return g;
  });
}
for (const [x, z] of [
  [-9.5, 26.6],
  [9.5, 26.6],
] as const) {
  prop(x, z, () => planter(1.4), { collide: { hx: 1.4, hy: 0.3, hz: 1.4 } });
}
/**
 * The staging table, dead ahead of where the thieves spawn.
 *
 * Placed here rather than anywhere more sensible because the opening frame of
 * the round has to contain the decision the round is about. You come up the
 * forecourt, the doors are ahead of you, and between you and them is a trestle
 * table with five tools on it and room in your bag for two.
 */
prop(-2.0, 28.2, () => toolBench(2.8), { collide: { hx: 1.4, hy: 0.45, hz: 0.42, cy: 0.45 } });
prop(-8.2, 30, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
prop(8.2, 30, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });
prop(11.5, 25.8, () => directoryPylon('CENTRE HOURS\n\nMON-SAT 9-6\nSUN 10-5\n\nCLOSED NOW'), {
  yaw: Math.PI,
  collide: { hx: 0.5, hy: 1.0, hz: 0.15 },
});

// ---------------------------------------------------------------------------
// The roof
// ---------------------------------------------------------------------------
// Deck level is CANOPY, and the slab collider's top face sits 0.5 above the
// zone's ceilingY (see the ceiling branch in plan.ts), so everything up here is
// authored to ROOF and nothing floats.
const ROOF = CANOPY + 0.5;

/**
 * THE BEAR.
 *
 * Seven metres, sitting, hands in its lap, looking south over the car park.
 *
 * Everything else in this file is a mall being a mall. This is the object that
 * tells you the mall is not the point. It is placed just west of the atrium
 * glazing rather than centred on it for two reasons: centred, it reads as
 * signage — a mascot, something the centre put there on purpose — and off to one
 * side it reads as something that arrived. And from the forecourt, west of the
 * glazing is where it eclipses the moon.
 *
 * Base at deck level with a twelve-metre wall in front of it, so from the ground
 * you see it from the ribbon up: no feet, no plinth, no explanation.
 */
prop(-17.5, 19.5, () => giantBear(8.5), { y: ROOF, yaw: -0.24 });
/**
 * Two floods at the parapet, aimed back and up at it.
 *
 * Non-negotiable rather than decorative. An unlit object against a night sky is
 * a hole in the sky, and a seven-metre hole reads as a missing texture, not as a
 * bear. Uplighting also puts the shadow in the wrong place for a face — under
 * the brow and the muzzle — which is most of why the thing is unnerving.
 */
for (const [x, z, tilt, drive] of [
  // Two at the parapet, wide, raking across the body from either side.
  [-23.2, 23.0, 0.85, 1.0],
  [-11.8, 23.0, 0.85, 1.0],
  // One dead in front, tucked between the feet and the parapet, throwing the
  // shadow of the muzzle up the face — the single detail that makes it a
  // presence rather than an ornament.
  [-17.5, 23.4, 1.15, 0.85],
] as const) {
  prop(x, z, () => roofFlood(tilt, drive), { y: ROOF, yaw: Math.PI });
}

/**
 * Plant. The roof has to look like it has a job.
 *
 * A bare deck with one enormous toy on it is a diorama; the same deck with
 * fourteen air handlers, a stair head, a dish and a mast on it is a building
 * that happens to have a toy on it, which is the reading that works.
 */
for (let i = 0; i < 6; i++) {
  prop(19.4, -6 + i * 5.2, () => condenserUnit(2.4, 1.3, 1.8), { y: ROOF });
  prop(22.2, -4 + i * 5.2, () => condenserUnit(1.8, 1.0, 1.5), { y: ROOF, yaw: 0.1 });
}
for (let i = 0; i < 4; i++) {
  prop(-20.5, -8 - i * 6.5, () => condenserUnit(2.2, 1.2, 1.7), { y: ROOF, yaw: Math.PI });
}
for (const [x, z] of [
  [17.2, -20],
  [17.2, 6],
  [-16.6, -30],
] as const) {
  prop(x, z, () => ductRun(9, 0.8), { y: ROOF + 0.5, yaw: Math.PI / 2 });
}
prop(-20.6, -24, () => stairPenthouse(3.2, 2.8, 2.8), { y: ROOF });
prop(21.6, -35, () => stairPenthouse(2.6, 2.4, 2.4), { y: ROOF, yaw: -0.3 });
prop(20.4, 15.5, () => satelliteDish(1.3), { y: ROOF, yaw: -2.2 });
prop(-22.4, 6, () => satelliteDish(0.8), { y: ROOF, yaw: -1.4 });
prop(22.6, -52, () => aerialMast(6.5), { y: ROOF });
prop(-22.6, -60, () => aerialMast(4.5), { y: ROOF });
// A working light at each stair head, so the roof is not a pure silhouette.
prop(-20.6, -21.4, () => roofFlood(0.45, 0.5), { y: ROOF });
prop(21.6, -32.8, () => roofFlood(0.45, 0.4), { y: ROOF });

// ---------------------------------------------------------------------------
// The car park
// ---------------------------------------------------------------------------

/**
 * THE PERIMETER.
 *
 * Colliders for these are declared with the walls as `invisible` runs; this is
 * only the fence you can see. Chain-link rather than hoarding on purpose — the
 * job of the edge of the world is to stop the player *and* to prove there is a
 * world on the other side of it, and a solid panel does exactly half of that.
 */
prop(-18.75, 64, () => chainLinkFence(30.5, 2.4, { barbed: true }));
prop(18.75, 64, () => chainLinkFence(30.5, 2.4, { barbed: true }));
prop(0, 64, () => lotGate(7, 2.4));
for (const s of [-1, 1]) {
  prop(s * 34, 44, () => chainLinkFence(40, 2.4, { barbed: true }), { yaw: Math.PI / 2 });
  prop(s * 29.25, Z_S, () => chainLinkFence(9.5, 2.4));
}

/**
 * THE WAY OUT, WHICH NOW HAS A SHAPE.
 *
 * The extraction point used to be an invisible four-metre disc on bare tarmac at
 * z = 35 — which the thieves also spawned standing inside. So the round opened
 * with the player already on the exit, and the exit itself was a HUD element
 * with no object under it. Both of those are the same bug: the level was making
 * the interface do its job.
 *
 * Moving it out to the apron fixes both at once. It is now a van with its back
 * doors open and its dome light on, a hundred metres of open ground from the
 * spawn, and the last twenty of that is lit tarmac with no cover on it. You can
 * see where you are going from the moment you walk in, and getting there with a
 * full bag is a decision rather than a formality.
 */
prop(0, 45.5, () => getawayVan(), {
  // Authored nose-first along +X with the load doors at -X, so this yaw and not
  // its negative: +pi/2 swings the doors to face +Z, out at the fence, and the
  // player walks the length of the apron to be shown the front bumper.
  yaw: -Math.PI / 2,
  collide: { hx: 1.1, hy: 1.4, hz: 2.6, cy: 1.4 },
});
prop(0, 41, () => forecourtMarkings(9, 7));

/**
 * Cars, bays, kerbs and trolleys.
 *
 * A hundred metres of empty tarmac is not a car park, it is a runway. The bays
 * give it grain, the kerb blocks give the grain a height, and the four cars are
 * the only thing out here that says the building was open today.
 */
for (const [z, n] of [
  [50, 11],
  [57, 11],
] as const) {
  prop(0, z, () => bayLines(n, 2.6, 5.0));
  for (let i = 0; i <= n; i++) prop((i - n / 2) * 2.6, z - 2.6, () => kerbBlock(1.7));
}
// The two side lots, marked out across the axis so the apron reads as three
// separate areas rather than one enormous rectangle.
for (const x of [-25.5, 25.5]) {
  prop(x, 31.5, () => bayLines(4, 2.6, 5.0), { yaw: Math.PI / 2 });
}
for (const [x, z, yaw, kind, tint] of [
  [-6.5, 49.6, 0, 'hatch', 0x4a5058],
  [3.9, 49.6, Math.PI, 'estate', 0x3b4a3e],
  [11.7, 56.6, Math.PI, 'hatch', 0x5a3b3b],
  [-24.2, 31.5, Math.PI / 2, 'van', 0x53585f],
  [26.8, 34.8, -Math.PI / 2, 'hatch', 0x2f3a48],
] as const) {
  prop(x, z, () => parkedCar(kind, tint), {
    yaw,
    collide: { hx: kind === 'van' ? 2.6 : 2.1, hy: 0.8, hz: 1.0, cy: 0.8 },
  });
}
prop(-13.5, 47.5, () => trolleyBay(9), { collide: { hx: 1.8, hy: 1.2, hz: 1.6, cy: 1.2 } });
prop(16.5, 54.5, () => trolleyBay(5), { yaw: Math.PI, collide: { hx: 1.8, hy: 1.2, hz: 1.6, cy: 1.2 } });
prop(-27, 61, () => pylonSign(9.5), { yaw: 0.3, collide: { hx: 1.1, hy: 1.5, hz: 0.6, cy: 1.5 } });
prop(20, 61.5, () => litterBin(), { collide: { hx: 0.35, hy: 0.5, hz: 0.35 } });

/**
 * Lamp columns down the lot, and the reason there are only six of them.
 *
 * Every one of these is baked, so reach is free but *count* is not — each is a
 * source the bake has to trace against every roof, car and fence out here. Six
 * on a ten-metre pitch covers the apron with overlapping pools, which is the
 * condition that stopped the forecourt showing a hard light line, and the same
 * rule applies out here for the same reason.
 */
for (const [x, z] of [
  [-19, 43],
  [19, 43],
  [-19, 54],
  [19, 54],
  [-8, 61],
  [8, 61],
] as const) {
  prop(x, z, () => lampPost(6.2, { realLight: true, pool: false }), {
    collide: { hx: 0.2, hy: 3.1, hz: 0.2 },
  });
}
// Skyglow over the lot, matching the forecourt's. Without it the tarmac out
// here is lit by sodium and nothing else, and thirty metres of uniform tan is
// the exact failure the forecourt was fixed for.
for (const [x, z] of [
  [-16, 46],
  [16, 46],
  [-16, 58],
  [16, 58],
  [0, 52],
] as const) {
  prop(x, z, () => {
    const g = new Group();
    const l = new PointLight(
      LIGHTS.skyglow.color,
      LIGHTS.skyglow.intensity,
      LIGHTS.skyglow.distance,
      LIGHTS.skyglow.decay,
    );
    l.position.y = 19;
    g.add(l);
    return g;
  });
}

/**
 * THE HORIZON.
 *
 * All of this is outside the fence and none of it is reachable — it exists so
 * that when you turn round at the van, the world does not stop. Silhouette
 * only: dark blocks with a scatter of lit windows, a treeline, and nothing with
 * a readable surface on it, because the moment one of these looks like a place
 * you could go, it becomes a place the player will try to go.
 */
for (const [x, z, w, h, d, seed] of [
  [-38, 82, 26, 15, 16, 3],
  [-6, 92, 34, 11, 18, 8],
  [40, 86, 22, 19, 15, 12],
  [-52, 60, 18, 13, 22, 21],
  [54, 52, 20, 16, 20, 29],
] as const) {
  prop(x, z, () => distantBlock(w, h, d, seed));
}
prop(-14, 71, () => treeLine(44, 5));
prop(30, 70, () => treeLine(26, 11));
prop(-42, 44, () => treeLine(32, 17), { yaw: Math.PI / 2 });
prop(44, 40, () => treeLine(28, 23), { yaw: Math.PI / 2 });

// ---------------------------------------------------------------------------
// Liminal beats, inside
// ---------------------------------------------------------------------------

/**
 * Two rides, a soft-play pen, a queue to nothing, and a balloon.
 *
 * Each is placed against the specific dead spot it is fixing. The stretch of
 * concourse between the doors and the escalators was the emptiest floor in the
 * building and it is the first thing the player walks through; the food court's
 * west end was tables and more tables; the escalator foot had no reason for the
 * escalators to be stopped.
 */
prop(-6.2, 18.5, () => kiddieRide(), { yaw: 0.5, collide: { hx: 0.7, hy: 0.9, hz: 0.9, cy: 0.9 } });
prop(6.8, -30.5, () => kiddieRide(), { yaw: -2.1, collide: { hx: 0.7, hy: 0.9, hz: 0.9, cy: 0.9 } });
prop(-14.5, -62, () => softPlay(6, 5), { collide: { hx: 3.1, hy: 0.5, hz: 2.6, cy: 0.5 } });
prop(8.4, 19.2, () => queueMaze(), { yaw: -0.3 });
prop(-8.6, 12.2, () => maintenanceHoarding(3.2, 2.0), {
  yaw: Math.PI,
  collide: { hx: 1.6, hy: 1.0, hz: 0.3, cy: 1.0 },
});
// Balloons: one under the atrium canopy, one over the food court. Both hang off
// structure that actually exists above them.
prop(4.6, 2.4, () => strayBalloon(2.6, 0xc8cfd8), { y: 11.6 });
prop(-7.2, -51.5, () => strayBalloon(1.9, 0xd8b8c4), { y: 10.2 });

// Cameras, as physical objects. The sim's camera list below has to agree with
// these positions, so they are generated from one table.
interface CamSpec {
  id: string;
  bank: string;
  x: number;
  z: number;
  y: number;
  yaw: number;
  pitch?: number;
  rangeM?: number;
  fovDeg?: number;
}

const CAMS: CamSpec[] = [
  { id: 'cam-atrium-s', bank: 'atrium', x: -11.5, z: 20, y: 4.4, yaw: Math.PI, rangeM: 30, fovDeg: 40 },
  { id: 'cam-atrium-m', bank: 'atrium', x: 11.5, z: 2, y: 4.4, yaw: Math.PI * 0.85, rangeM: 26 },
  { id: 'cam-atrium-n', bank: 'atrium', x: -11.5, z: -30, y: 4.4, yaw: 0, rangeM: 28 },
  { id: 'cam-bridge', bank: 'atrium', x: 0, z: 8.5, y: 10.5, yaw: Math.PI, pitch: -0.6, rangeM: 22 },
  { id: 'cam-gallery-w', bank: 'gallery', x: -11.5, z: -8, y: L1 + 3.4, yaw: 0, rangeM: 26 },
  { id: 'cam-gallery-e', bank: 'gallery', x: 11.5, z: -22, y: L1 + 3.4, yaw: Math.PI, rangeM: 26 },
  { id: 'cam-svc-w0', bank: 'service', x: -22.75, z: 10, y: 2.6, yaw: Math.PI, rangeM: 24, fovDeg: 26 },
  { id: 'cam-svc-e0', bank: 'service', x: 22.75, z: -34, y: 2.6, yaw: 0, rangeM: 24, fovDeg: 26 },
  { id: 'cam-svc-w1', bank: 'service', x: -22.75, z: -20, y: L1 + 2.6, yaw: Math.PI, rangeM: 24, fovDeg: 26 },
  { id: 'cam-foodcourt', bank: 'foodcourt', x: -19, z: -46, y: 5.0, yaw: -Math.PI * 0.75, rangeM: 30, fovDeg: 44 },
  { id: 'cam-dock', bank: 'dock', x: 26.5, z: -24, y: 5.0, yaw: Math.PI * 0.85, rangeM: 24 },
  { id: 'cam-plaza', bank: 'plaza', x: 0, z: 26, y: 5.2, yaw: 0, rangeM: 26, fovDeg: 46 },
];

for (const c of CAMS) {
  props.push({
    x: c.x,
    z: c.z,
    y: c.y,
    yaw: c.yaw,
    build: () => securityCamera(),
  });
}

// ---------------------------------------------------------------------------
// Patrol routes
// ---------------------------------------------------------------------------
/**
 * Three beats, and between them they see the whole building.
 *
 * The routes were laid out around what the cameras cannot do. Cameras are static
 * cones on fixed banks — they cover the atrium spine, the two galleries and the
 * dock, and they cover almost nothing inside a shop unit or along the far ends of
 * the service corridors. So the ground beat walks in and out through the back
 * doors rather than staying in the concourse, the upper beat crosses the void
 * twice a lap so a thief cannot simply orbit the gallery ahead of it, and the
 * back-of-house beat exists entirely to make the unwatched half of the mall cost
 * something. The food court, which no route lingers in, is the deliberate hole:
 * every level needs one place that is genuinely quiet, and it is the furthest
 * point from the shops that are worth robbing.
 *
 * The points are floor positions, not eye positions. Consecutive points must be
 * connected on the navigation grid; `tools/play.ts` asserts that, because a
 * patrol node inside a planter looks completely fine in a screenshot and puts a
 * guard into a wall for the rest of the round.
 */
const PATROLS: PatrolRoute[] = [
  {
    id: 'ground',
    name: 'Ground',
    at: [
      { x: -22.8, y: 0, z: 4 },
      { x: -22.8, y: 0, z: 16 },
      { x: -17, y: 0, z: 13.5 }, // in through the south-west unit's back door
      { x: -9, y: 0, z: 16 },
      { x: 0, y: 0, z: 18.2 }, // just inside the south doors, clear of the planter
      { x: 9, y: 0, z: 14 },
      { x: 17, y: 0, z: 16.5 }, // out through the skate unit
      { x: 22.8, y: 0, z: 16 },
      { x: 22.8, y: 0, z: -12 },
      { x: 17, y: 0, z: -24.4 },
      { x: 9, y: 0, z: -30 },
      { x: 0, y: 0, z: -40 }, // the mouth of the food court
      { x: -9, y: 0, z: -34 },
      { x: -9, y: 0, z: -14 },
      { x: -9, y: 0, z: 2 },
    ],
  },
  {
    id: 'upper',
    name: 'Level Two',
    at: [
      { x: 9, y: L1, z: 14 },
      { x: 9, y: L1, z: -2 },
      { x: 0, y: L1, z: 1 }, // the bridge, watching both escalators
      { x: -9, y: L1, z: -6 },
      { x: -17, y: L1, z: -11.8 },
      { x: -22.8, y: L1, z: -14 }, // head of the west service stair
      { x: -22.8, y: L1, z: 8 },
      { x: -17, y: L1, z: 11.9 },
      { x: -9, y: L1, z: 14 },
      { x: 0, y: L1, z: 1 },
      { x: 9, y: L1, z: -16 },
      { x: 9, y: L1, z: -32 },
      { x: 0, y: L1, z: -46 }, // the balcony over the food court
      { x: -9, y: L1, z: -34 },
      { x: -9, y: L1, z: -18 },
    ],
  },
  {
    id: 'backofhouse',
    name: 'Back of House',
    at: [
      { x: -28, y: 0, z: -3 }, // the control room itself
      { x: -22.8, y: 0, z: -6 },
      { x: -22.8, y: 0, z: -24 },
      { x: -22.8, y: 0, z: -40 },
      { x: -19.5, y: 0, z: -52 }, // the food court chute
      { x: -4, y: 0, z: -56 },
      { x: 12, y: 0, z: -52 },
      { x: 22.8, y: 0, z: -40 },
      { x: 22.8, y: 0, z: -31 }, // the dock door
      { x: 30, y: 0, z: -28 },
      { x: 22.8, y: 0, z: -22 },
      { x: 22.8, y: 0, z: -6 },
      { x: 17, y: 0, z: 7.4 },
      { x: 9, y: 0, z: 4 },
      { x: 0, y: 0, z: -3.6 }, // the middle of the atrium, clear of the planter
      { x: -9, y: 0, z: -2 },
      { x: -17, y: 0, z: -6.2 },
    ],
  },
];

// ---------------------------------------------------------------------------
// The plan
// ---------------------------------------------------------------------------

export const MALL: MallPlan = {
  name: 'meridian-crossing',
  zones,
  walls,
  runs,
  props,
  stairs,
  dropoffs: [
    // Solo chutes. One per service corridor plus one in the food court: always
    // near, always worse than the dock.
    { id: 'chute-w', kind: 'solo', x: -23.6, z: -2, radius: 2.2, yaw: Math.PI / 2, build: pneumaticChute },
    { id: 'chute-e', kind: 'solo', x: 23.6, z: -14, radius: 2.2, yaw: -Math.PI / 2, build: pneumaticChute },
    { id: 'chute-fc', kind: 'solo', x: -19.5, z: -55, radius: 2.2, yaw: Math.PI / 2, build: pneumaticChute },
    { id: 'chute-l1', kind: 'solo', x: -23.6, z: 12, y: L1, radius: 2.2, yaw: Math.PI / 2, build: pneumaticChute },
    // The dock. Furthest point in the building from every shop, needs two people,
    // and pays for both of those facts.
    { id: 'dock', kind: 'dock', x: 31, z: -31, radius: 3.2, yaw: -Math.PI / 2, build: () => dockPlatform(5.5) },
    // The way out. Out on the apron behind the forecourt, at the van — it used
    // to be at z = 35, which is inside the ring of bollards and, worse, on top
    // of the thief spawns.
    { id: 'exit-plaza', kind: 'extraction', x: 0, z: 42.5, radius: 4.0 },
  ],
  cameras: CAMS.map((c) => ({
    id: c.id,
    bank: c.bank,
    x: c.x,
    z: c.z,
    y: c.y,
    yaw: c.yaw,
    pitch: c.pitch,
    fovDeg: c.fovDeg,
    rangeM: c.rangeM,
  })),
  spawns: {
    /**
     * Thieves come in from the plaza, at the south doors.
     *
     * Pulled forward to the bollard line. They used to stand at z 30–33, which
     * after the extraction moved would have put the whole crew a step away from
     * the van — the round would open with the exit behind you, which is the
     * wrong first fact about a heist. Now the van is visibly forty metres at
     * your back and the doors are ten metres ahead, in that order.
     */
    thieves: [
      { x: -3.5, y: 0, z: 29 },
      { x: -1.2, y: 0, z: 30.2 },
      { x: 1.2, y: 0, z: 30.2 },
      { x: 3.5, y: 0, z: 29 },
      { x: 0, y: 0, z: 31.6 },
      { x: -6.2, y: 0, z: 30.6 },
    ],
    // Guards start where the cameras are.
    guards: [
      { x: -29, y: 0, z: -3 },
      { x: -29, y: 0, z: 0 },
      { x: 31, y: 0, z: -28 },
    ],
  },
  patrols: PATROLS,
};

/** Signage colour used for the plaza sign and the loading bay. Exported so the
 *  UI can match the building rather than inventing its own palette. */
export const MALL_SIGN_COLOR = EMIT.signCyan;
